import { useApolloClient } from '@apollo/client'
import { Theme } from '@eversports/react-components/design-tokens/theme'
import CircularProgress from '@eversports/react-components/primitives/CircularProgress'
import Flex from '@eversports/react-components/primitives/Flex'
import Placeholder from '@eversports/react-components/primitives/Placeholder'
import ClientLogger from '@eversports/react-components/utilities/client-logger'
import { makeStyles } from '@mui/styles'
import { parse } from 'query-string'
import React, { useContext, useEffect, useState } from 'react'
import { RouteComponentProps, withRouter } from 'react-router'
import RegistrationContext from '../../registration-context'

import InvalidURL from '../InvalidURL'
import Stepper from '../Stepper'
import { ValidationFailed } from '../ValidationFailed'

import validateAndLoadByToken from './validate-and-load-by-token'

type TokenState = 'checking' | 'valid' | 'invalid' | 'failed'

interface UsageDetails {
  usedAt?: string | null
  usedForCompanyName?: string | null
}

const useStyles = makeStyles(({ breakpoints }: Theme) => ({
  content: {
    margin: '0 auto',
    maxWidth: '100%',
    [breakpoints.up('md')]: {
      maxWidth: 1400,
    },
  },
  innerContent: {
    maxWidth: '100%',
    [breakpoints.up('md')]: {
      maxWidth: 600,
    },
  },
}))

const MatchedRegistrationRouteLayout: React.FC<React.PropsWithChildren<RouteComponentProps>> = ({
  children,
  location,
}) => {
  const classes = useStyles()
  const { token } = parse(location.search)
  const [tokenState, setTokenState] = useState<TokenState>('checking')
  const [usageDetails, setUsageDetails] = useState<UsageDetails>({})
  const registrationContext = useContext(RegistrationContext)
  const client = useApolloClient()

  if (!registrationContext) {
    throw new Error('The wrapping context was not correctly defined')
  }

  useEffect(() => {
    checkToken().catch((error) => {
      ClientLogger.error('Token validation Failed', error)
      setTokenState('failed')
    })
  }, [token])

  const checkToken = async () => {
    if (!token) {
      setTokenState('invalid')
      return
    }

    try {
      const result = await validateAndLoadByToken(client)({ token: String(token) })
      if (result.isError) {
        throw result.error
      }
      if (result.value.__typename === 'ValidateAndLoadByTokenResult') {
        setTokenState(result.value.isValid ? 'valid' : 'invalid')
        setUsageDetails({
          usedAt: result.value.usedAt,
          usedForCompanyName: result.value.usedForCompanyName,
        })
        registrationContext.setUserAdminInformation({
          ...registrationContext.userAdminInformation,
          email: result.value.customerAdminMail!,
        })
      }
    } catch (error: any) {
      ClientLogger.error('Token validation Failed', error)
      setTokenState('failed')
    }
  }

  const renderContent = () => {
    if (tokenState === 'checking') {
      return (
        <Placeholder height={250} width="100%" center>
          <CircularProgress />
        </Placeholder>
      )
    }

    if (tokenState === 'invalid') {
      return <InvalidURL {...usageDetails} />
    }

    if (tokenState === 'failed') {
      return <ValidationFailed />
    }

    return (
      <>
        <Stepper />
        <Flex flexDirection="column" className={classes.innerContent} fullWidth>
          {children}
        </Flex>
      </>
    )
  }

  return (
    <Flex
      className={classes.content}
      flexDirection="column"
      justifyContent="flex-start"
      alignItems="center"
      padding="default"
      paddingBottom="wide"
      verticalSpacing="wide"
      flexGrow={1}
      flexShrink={0}
    >
      {renderContent()}
    </Flex>
  )
}

export default withRouter(MatchedRegistrationRouteLayout)
